<template>
  <form ref="form" class="form-login-v2" @submit.prevent="submit">
    <app-overlay :show="busy" variant="dark">
      <template v-if="!pendingToken">
        <div class="row g-2">
          <div class="username-field col-12">
            <input
              id="login-username"
              v-model="usernameOrEmail"
              name="username"
              type="text"
              class="form-control"
              :placeholder="t('Username or Email')"
              required
              autocomplete="username"
              autocapitalize="off"
              spellcheck="false"
            />
          </div>

          <div class="password-field col-12">
            <input
              id="login-password"
              v-model="password"
              name="password"
              type="password"
              class="form-control"
              :placeholder="t('Password')"
              required
              autocomplete="current-password"
            />
          </div>

          <div v-if="error" class="invalid-feedback d-block text-center">
            {{ error }}
          </div>

          <div class="remember-me-field col-12 mb-4">
            <div class="form-check">
              <input id="remember-me-checkbox" class="form-check-input" type="checkbox" />
              <label class="form-check-label" for="remember-me-checkbox">
                {{ t("Remember me. I confirm this is not a shared device") }}
              </label>
            </div>
          </div>
        </div>

        <button class="btn btn-primary border-dark w-100" type="submit">
          {{ t("Sign In") }}
        </button>
      </template>

      <template v-else>
        <p class="text-center mb-3">{{ t("Enter the 6-digit code from your authenticator app") }}</p>
        <div class="row g-2">
          <div class="col-12">
            <input
              id="login-2fa-code"
              v-model="twoFactorCode"
              name="code"
              type="text"
              inputmode="numeric"
              pattern="[0-9]{6}"
              maxlength="6"
              class="form-control text-center"
              :placeholder="t('6-digit code')"
              required
              autocomplete="one-time-code"
            />
          </div>

          <div v-if="error" class="invalid-feedback d-block text-center">
            {{ error }}
          </div>
        </div>

        <button class="btn btn-primary border-dark w-100 mt-3" type="submit">
          {{ t("Verify") }}
        </button>

        <button type="button" class="btn btn-blue border-dark w-100 mt-1" @click="cancelTwoFactor">
          {{ t("Back") }}
        </button>
      </template>
    </app-overlay>
  </form>
</template>

<script setup lang="ts">
import { ref, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useI18n } from "vue-i18n";
import AppOverlay from "@/components/v2/app-overlay";
import authService from "@/api/services/authService";
import { setAccessToken } from "@/utils/storage";
import { amplitudeTrack } from "@/utils/amplitude";
import { AUTH_ERROR_MULTIPLE_USERS } from "@/utils/variables.ts";
import { useAuthStore } from "@/store/auth";

defineOptions({
  name: "form-login",
});

const router = useRouter();
const route = useRoute();
const { t } = useI18n();
const authStore = useAuthStore();

const form = ref<HTMLFormElement | null>(null);
const usernameOrEmail = ref<string | null>(null);
const password = ref<string | null>(null);
const twoFactorCode = ref<string | null>(null);
const pendingToken = ref<string | null>(null);
const busy = ref(false);
const error = ref<string | null>(null);

watch(
  () => authStore.pending2faToken,
  (token) => {
    if (token) {
      pendingToken.value = token;
      authStore.setPending2faToken(null);
    }
  },
  { immediate: true },
);

const cancelTwoFactor = () => {
  pendingToken.value = null;
  twoFactorCode.value = null;
  error.value = null;
};

const submit = async () => {
  if (!form.value?.checkValidity()) {
    form.value?.reportValidity();
    return;
  }

  if (busy.value) {
    return;
  }

  busy.value = true;
  error.value = null;

  try {
    if (pendingToken.value) {
      const token = await authService.verify2fa(pendingToken.value, twoFactorCode.value as string);
      setAccessToken(token);
      await router.push((route.query.redirect as string) || "/?login=true");
      return;
    }

    amplitudeTrack("Popup login submit");

    const result = await authService.login(usernameOrEmail.value as string, password.value as string);

    if (typeof result === "object" && result.requires_2fa) {
      pendingToken.value = result.pending_token;
      return;
    }

    setAccessToken(result as string);
    await router.push((route.query.redirect as string) || "/?login=true");
  } catch (e: any) {
    const status = e.status?.status;

    if (status === 401) {
      error.value = pendingToken.value
        ? t("Invalid code. Please try again.")
        : e.status?.message === AUTH_ERROR_MULTIPLE_USERS
          ? t("We can’t log you in using your email. Please use your username.")
          : t("Wrong username or password");
    } else {
      error.value = t("Something went wrong");
      throw e;
    }
  } finally {
    busy.value = false;
  }
};
</script>
